import api from './api';

const userService = {
  /**
   * Get current user's profile
   */
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  /**
   * Update current user's profile
   * @param {Object} profileData - Profile fields to update
   * @param {string} [profileData.display_name] - Display name
   * @param {string} [profileData.timezone] - Timezone (e.g., 'Australia/Brisbane')
   * @param {string} [profileData.date_format] - Date format (e.g., 'DD/MM/YYYY')
   */
  updateProfile: async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    return response.data;
  },

  /**
   * Change current user's password
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.currentPassword - Current password
   * @param {string} passwordData.newPassword - New password (min 8 chars)
   * @param {string} passwordData.confirmPassword - Confirmation of new password
   */
  changePassword: async (passwordData) => {
    const response = await api.put('/users/password', passwordData);
    // Item 89 — the backend bumps the session epoch (logging out every OTHER
    // device) and hands back a fresh token stamped with the new epoch. Swap
    // it in so THIS session keeps working; without this, the next request
    // would carry the now-stale token and get bounced to /login.
    const newToken = response.data?.data?.token;
    if (newToken) {
      localStorage.setItem('token', newToken);
    }
    return response.data;
  },

  /**
   * Mark onboarding as completed for the current user. Bounded: the flag is a
   * formality and must never hold the wizard hostage (Louise, 18 Sep 2026 —
   * the final button spun for minutes on a stalled request).
   */
  completeOnboarding: async () => {
    // An empty object, not null: a null body serialises to the JSON text
    // "null", which the server's strict JSON parser rejects with a 400.
    const response = await api.put('/users/onboarding/complete', {}, { timeout: 8000 });
    return response.data;
  },

  /**
   * Make sure the onboarding flag lands on the server, without the user
   * waiting for it. Tries now; on failure leaves a marker and retries in the
   * background with growing gaps. The marker survives a reload — see
   * retryPendingOnboardingComplete(), called at app start. Resolves true
   * when the server has confirmed it, false when it is still pending.
   */
  ensureOnboardingComplete: async () => {
    const MARKER = 'onboarding_complete_pending';
    const attempt = async () => {
      await userService.completeOnboarding();
      try { localStorage.removeItem(MARKER); } catch (_) { /* ignore */ }
      return true;
    };
    try {
      return await attempt();
    } catch (_) {
      try { localStorage.setItem(MARKER, '1'); } catch (_) { /* ignore */ }
      const gaps = [5000, 15000, 45000, 120000];
      const later = (i) => {
        if (i >= gaps.length) return;
        setTimeout(async () => {
          try { if (localStorage.getItem(MARKER)) await attempt(); } catch (_) { later(i + 1); }
        }, gaps[i]);
      };
      later(0);
      return false;
    }
  },

  /**
   * At app start: if a previous session left the flag pending and the user
   * is still signed in, try again quietly.
   */
  retryPendingOnboardingComplete: () => {
    try {
      if (!localStorage.getItem('token') || !localStorage.getItem('onboarding_complete_pending')) return;
    } catch (_) { return; }
    userService.ensureOnboardingComplete().catch(() => { /* background */ });
  },

  /**
   * Get getting started checklist status
   */
  getGettingStartedStatus: async () => {
    const response = await api.get('/users/getting-started-status');
    return response.data;
  },

  /**
   * Dismiss the getting started checklist
   */
  dismissGettingStarted: async () => {
    const response = await api.put('/users/getting-started/dismiss');
    return response.data;
  }
};

export default userService;
